import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { IconButton, OptionThumbnailLoader } from "~components";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

type CarouselProps = {
  /* Carousel for uniform width items, Currently carousel pagination is based of the
  width of the first item */
  items: React.ReactNode[];
  /** number of item lengths to scroll; Defaults to 1 */
  increment?: number;
};

/**
 * @param inc - number of item lengths to scroll; Defaults to 1
 */
export type CarouselRef = {
  forward: (inc?: number) => void;
  backward: (inc?: number) => void;
};

// FIXME: Resizing the window messes up the scroll calculations
export const Carousel = forwardRef((props: CarouselProps, ref: ForwardedRef<CarouselRef>) => {
  const { items, increment = 1 } = props;
  // item container ref
  const containerRef = useRef<HTMLDivElement>(null);
  // itemref determines the width of the first item to set how far to scroll
  const firstItemRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const parentControlled = ref !== null;
  const tid = useTestIds(props, "carousel");

  // Handlers
  const scrollX = (direction: "left" | "right", inc = increment) => {
    if (!containerRef.current || !firstItemRef.current) return;

    const scrollAmount = (firstItemRef.current.clientWidth || 0) * inc;
    const scrollDirection = direction === "left" ? -scrollAmount : scrollAmount;

    containerRef.current.scrollBy({
      left: scrollDirection,
      behavior: "smooth",
    });
  };

  // Update scroll button states based on actual scroll position
  const updateScrollButtons = () => {
    if (!containerRef.current) return;

    const currentScrollLeft = containerRef.current.scrollLeft;
    const scrollWidth = containerRef.current.scrollWidth;
    const visibleWidth = containerRef.current.clientWidth;

    setCanScrollLeft(currentScrollLeft > 0);
    setCanScrollRight(currentScrollLeft + visibleWidth < scrollWidth);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [parentControlled]);

  // Listen for scroll events to update button states
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollButtons);
    return () => container.removeEventListener("scroll", updateScrollButtons);
  }, []);

  useImperativeHandle(ref, () => ({
    forward: (inc) => {
      scrollX("right", inc);
    },
    backward: (inc) => {
      scrollX("left", inc);
    },
  }));

  return (
    <div css={Css.df.aic.w100.relative.if(parentControlled).w100.oh.$} {...tid}>
      {/* Always overlaps with item and only shows when there is room to scroll */}
      {!parentControlled && (
        <div
          css={{
            ...Css.absolute.left0.top0.bottom0.aic.z3.bgWhite.if(!canScrollLeft).o0.add("pointerEvents", "none").$,
            ...Css.ifMobile.dn.else.df.$,
          }}
        >
          <IconButton
            hideBorder
            icon="chevron-left"
            onClick={() => scrollX("left")}
            size="small"
            shape="square"
            {...tid.left}
          />
        </div>
      )}
      {/* item container */}
      <div
        id="carousel-item-container"
        ref={containerRef}
        css={{
          ...Css.df.gap1.overflowX("hidden").add("scrollSnapType", "x mandatory").add("scrollBehavior", "smooth").$,
          ...Css.ifTabletAndDown.oxs.oahs.$,
        }}
      >
        {items.map((item, index) => (
          <span ref={index === 0 ? firstItemRef : undefined} css={Css.add("scrollSnapAlign", "start").$} key={index}>
            {item}
          </span>
        ))}
      </div>
      {!parentControlled && (
        <div
          css={{
            ...Css.absolute.right0.top0.bottom0.aic.z3.bgWhite.if(!canScrollRight).o0.add("pointerEvents", "none").$,
            ...Css.ifMobile.dn.else.df.$,
          }}
        >
          <IconButton
            hideBorder
            icon="chevron-right"
            onClick={() => scrollX("right")}
            size="small"
            shape="square"
            {...tid.right}
          />
        </div>
      )}
    </div>
  );
});

type LoadingCarouselProps = {
  count: number;
  size: number;
};

export function LoadingCarousel({ count, size }: LoadingCarouselProps) {
  const placeholderKeys = useMemo(() => Array.from({ length: count }, (_, index) => `loading-${index}`), [count]);
  return (
    <div css={Css.df.gap1.$}>
      {placeholderKeys.map((key) => (
        <OptionThumbnailLoader key={key} size={size} />
      ))}
    </div>
  );
}
